import React from "react";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { SecondaryButton, SwitchField } from "@ucl/library";
import "./FinancialApplicationFormSectionSummary.scss";
import { useNavigate } from "react-router-dom";

export enum FinancialApplicationFormSectionSummaryDataRowType {
  LabelValue = "label-value",
  LabelToggleValue = "label-toggle-value",
  LabelTableValue = "label-table-value",
}

export interface FinancialApplicationFormSectionSummaryLabelValueRow {
  label: string | React.ReactNode;
  value: string;
  type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue;
}

export interface FinancialApplicationFormSectionSummaryLabelToggleValueRow {
  label: string | React.ReactNode;
  value: boolean;
  type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue;
}

export interface FinancialApplicationFormSectionSummaryLabelTableValueRow {
  label: string | React.ReactNode;
  data: any[];
  orderedKeysAndHeaderLabels: {
    key: string;
    headerLabel: string;
  }[];
  type: FinancialApplicationFormSectionSummaryDataRowType.LabelTableValue;
}

export type FinancialApplicationFormSectionSummaryDataRow = FinancialApplicationFormSectionSummaryLabelValueRow | FinancialApplicationFormSectionSummaryLabelToggleValueRow | FinancialApplicationFormSectionSummaryLabelTableValueRow;

export interface FinancialApplicationFormSectionSummaryProps {
  title: string;
  data: FinancialApplicationFormSectionSummaryDataRow[];
  navigateTo?: string;
  onModify?: () => void;
  children?: React.ReactNode;
}

export const FinancialApplicationFormSectionSummary: React.FC<FinancialApplicationFormSectionSummaryProps> = ({
  title,
  data,
  navigateTo,
  onModify,
  children,
}) => {
    const navigate = useNavigate();
    return (
    <div className="financial-application-form-section-summary">
      <div className="financial-application-form-section-summary__header">
        <h3 className="financial-application-form-section-summary__title">{title}</h3>
        <SecondaryButton
          text="Modify"
          onClick={() => {
            if (navigateTo) {
              navigate(navigateTo);
            } else {
              onModify?.();
            }
          }}
          cssClass="financial-application-form-section-summary__modify-btn"
          iconPosition="left"
          iconProps={{ icon: faPencil }}
        />
      </div>
      
      <div className="financial-application-form-section-summary__content">
        {/* Render data first if available */}
        {data.length > 0 && data.map((item, index) => (
          <div key={index} className="financial-application-form-section-summary__item">
            <span className="financial-application-form-section-summary__label">{item.label}</span>
            {item.type === FinancialApplicationFormSectionSummaryDataRowType.LabelValue && (
              <span className="financial-application-form-section-summary__value">{item.value}</span>
            )}
            {item.type === FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue && (
              <span className="financial-application-form-section-summary__value">
                <SwitchField value={item.value} readOnly disabled />
              </span>
            )}
            {item.type === FinancialApplicationFormSectionSummaryDataRowType.LabelTableValue && (
              <div className="financial-application-form-section-summary__table">
                <table>
                  <thead>
                    <tr>
                      {item.orderedKeysAndHeaderLabels.map((keyAndHeaderLabel) => (
                        <th key={keyAndHeaderLabel.key}>{keyAndHeaderLabel.headerLabel}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {item.data.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {item.orderedKeysAndHeaderLabels.map((keyAndHeaderLabel) => (
                          <td key={keyAndHeaderLabel.key}>{row[keyAndHeaderLabel.key]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
        
        {/* Render children if provided */}
        {children}
      </div>
    </div>
  );
}; 